import * as React from 'react'
import { useState, type FormEvent } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { resetPassword } from '@/features/auth/api'
import { ApiError } from '@/lib/api'
import { Button } from '@/components/primitives/Button'
import { FormField } from '@/components/primitives/FormField'
import { PasswordInput } from '@/components/primitives/PasswordInput'
import { useToast } from '@/components/feedback/ToastProvider'
import './LoginPage.css'

export function ResetPasswordPage(): React.JSX.Element {
  const [params] = useSearchParams()
  const navigate = useNavigate()
  const toast = useToast()
  const token = params.get('token') ?? ''

  const [newPassword, setNewPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [fieldError, setFieldError] = useState('')

  if (!token) {
    return (
      <div className="login-page login-page--simple">
        <main className="login-form-side login-form-side--full">
          <div className="login-form-wrap">
            <h1 className="login-title">Invalid link</h1>
            <p className="login-sub">This reset link is missing or malformed. Request a new one.</p>
            <Link to="/forgot-password" className="forgot-back-btn">
              Request reset link
            </Link>
          </div>
        </main>
      </div>
    )
  }

  async function onSubmit(e: FormEvent): Promise<void> {
    e.preventDefault()
    setFieldError('')

    if (newPassword.length < 8) {
      setFieldError('Password must be at least 8 characters.')
      return
    }
    if (newPassword !== confirm) {
      setFieldError('Passwords do not match.')
      return
    }

    setSubmitting(true)
    try {
      await resetPassword(token, newPassword)
      toast.success('Password updated', 'You can now sign in with your new password.')
      navigate('/login', { replace: true })
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.code === 'token_expired') {
          setFieldError('This link has expired. Please request a new one.')
        } else if (err.code === 'token_invalid') {
          setFieldError('This link is invalid or has already been used.')
        } else {
          setFieldError(err.message || 'Something went wrong. Try again.')
        }
      } else {
        setFieldError('Something went wrong. Try again.')
      }
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="login-page login-page--simple">
      <main className="login-form-side login-form-side--full">
        <div className="login-form-wrap">
          <div className="login-back-link">
            <Link to="/login">← Back to sign in</Link>
          </div>

          <h1 className="login-title">Set new password</h1>
          <p className="login-sub">Choose a new password for your account.</p>

          <form className="login-form" onSubmit={(e) => { void onSubmit(e) }} noValidate>
            <FormField
              label="New password"
              htmlFor="reset-pw"
              required
              hint="At least 8 characters."
              error={fieldError || undefined}
            >
              <PasswordInput
                id="reset-pw"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                autoComplete="new-password"
                autoFocus
                invalid={!!fieldError}
              />
            </FormField>
            <FormField label="Confirm password" htmlFor="reset-pw-confirm" required>
              <PasswordInput
                id="reset-pw-confirm"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                autoComplete="new-password"
                invalid={!!fieldError && newPassword !== confirm}
              />
            </FormField>
            <Button type="submit" loading={submitting} className="login-submit">
              Set password
            </Button>
          </form>
        </div>
      </main>
    </div>
  )
}
